import AppLayout from '@/layout/AppLayout.vue';
import { createRouter, createWebHistory } from 'vue-router';
import { useDuoAuthStore } from '@/stores/duoAuthStore';
import { DuoService } from '@/services/DuoService';

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/',
            component: AppLayout,
            children: [
                {
                    path: '/',
                    name: 'account-details',
                    component: () => import('../views/AccountDetailsPage.vue')
                },
                {
                    path: '/direct-deposits',
                    name: 'direct-deposits',
                    component: () => import('../views/DirectDepositPage.vue'),
                    meta: { requiresDuoAuth: true }
                },
                {
                    path: '/expense-reimbursements',
                    name: 'expense-reimbursements',
                    component: () => import('../views/ExpenseReimbursementPage.vue'),
                    meta: { requiresDuoAuth: true }
                }
            ]
        },
        {
            path: '/:pathMatch(.*)*',
            redirect: '/'
        }
    ]
});

// Router guard for Duo authentication
router.beforeEach((to, _from, next) => {
    //Disable for dev env
    if (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'dev' || import.meta.env.MODE === 'test') {
        next();
        return;
    }

    // Check if route requires Duo authentication
    if (to.meta.requiresDuoAuth) {
        const duoService = DuoService.getInstance();

        // Allow Duo callback redirects (these have query params from Duo)
        if (duoService.isAuthCallback()) {
            next();
            return;
        }

        // Check if user has recent Duo authentication for this action
        const duoStore = useDuoAuthStore();

        // Determine expected action based on route
        const routeName = String(to.name || '');
        const expectedActions = getExpectedActionsForRoute(routeName);

        // Allow access if user has completed a relevant Duo action recently
        if (duoStore.hasValidAuthForActions(expectedActions)) {
            next();
            return;
        }

        // Tell the user when Duo sent them back without a usable result, instead of redirecting silently
        const failedReturnReason = duoService.getFailedReturnReason();
        if (failedReturnReason) {
            duoService.clearPendingAuth();
            duoStore.handleDuoError(failedReturnReason);
        }

        // Redirect to account details if no valid authentication
        next({ name: 'account-details' });
        return;
    }

    // Allow access to non-protected routes
    next();
});

// Helper function to determine expected Duo actions for each route
function getExpectedActionsForRoute(routeName: string): string[] {
    const actionMap: Record<string, string[]> = {
        'direct-deposits': ['add direct deposit account', 'edit direct deposit account', 'update direct deposit account', 'add direct deposit secondary account', 'edit direct deposit main account', 'update direct deposit main account'],
        'expense-reimbursements': ['add expense reimbursement account', 'edit expense reimbursement account']
    };

    return actionMap[routeName] || [];
}

export default router;
